/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { getPublicSiteUrl } from "@cosmos/common-utils";
import { CosmosComponentContext, CosmosComponentContextProps, GameModeContext } from "@cosmos/components";
import { EDSProvider, lowDensityThemeClass } from "@eds/react";
import { LocationProvider } from "@epic-mw/app-context";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

interface Props {
	children?: React.ReactNode;
	componentContext?: CosmosComponentContextProps;
	/**
	 * `?mode=game` / `?mode=iosgame`. Provided as context so every CosmosRichText
	 * in the tree neutralises its links, including rich text rendered by
	 * components that don't forward the flag. See docs/game-mode.md.
	 */
	isGameMode?: boolean;
}

export const TemplateProviders = ({ children, componentContext, isGameMode }: Props): React.JSX.Element => {
	const [hash, setHash] = useState("");
	useEffect(() => {
		if (typeof window !== "undefined" && window?.location?.hash && hash !== window?.location?.hash) {
			setHash(window.location.hash);
		}
	}, [hash]);
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();
	const searchStr = search && search.length ? `?${search}` : "";
	// getPublicSiteUrl() returns origin + base path (e.g., "https://www.fab.com/o").
	// usePathname() returns the path relative to basePath (e.g., "/en-US/page"), so no double-prefix.
	const urlStr = `${getPublicSiteUrl()}${pathname}${searchStr}${hash}`;
	// Memoized on the string, not rebuilt per render. LocationProvider's useMemo
	// keys on this object, so a fresh URL each render gave every consumer a new
	// reference — re-firing useFormatHtml's cleanup effect and re-running the
	// formatHtml pipeline for every rich-text block on the page, on all traffic.
	const location = useMemo(() => new URL(urlStr), [urlStr]);

	const content = componentContext ? (
		<CosmosComponentContext.Provider value={componentContext}>{children}</CosmosComponentContext.Provider>
	) : (
		<>{children}</>
	);

	return (
		// noI18nProvider: without it EDSProvider wraps every child in a
		// <Suspense fallback={null}> around a lazy() react-aria I18nProvider. A lazy
		// import is always pending on the server, so that boundary was always
		// deferred out of the shell and took the entire page with it — legalhub
		// served `<div id="eula-key-template"><!--$?--><template id="B:0">` with no
		// body and inserted it with an $RC script after the shell had painted.
		//
		// Safe to drop because we never pass `locale`: EDS was feeding react-aria a
		// module-scope `navigator.language`, and with no provider react-aria's own
		// useDefaultLocale converges on the same value — via an SSR-gated,
		// Intl-validated path, so hydration is if anything safer. `dir` is applied by
		// a separate DirectionProvider that this flag doesn't touch. Caveat for later:
		// with the flag set, a `locale` prop would reach EDS's own components but no
		// longer reach the react-aria-driven ones.
		<EDSProvider dir={"ltr"} themeClass={lowDensityThemeClass} noI18nProvider>
			<GameModeContext.Provider value={!!isGameMode}>
				{/*
				  Deliberately no Suspense boundary around `content`. Adding one to replace
				  the incidental boundary EDS used to provide re-broke this ticket: page
				  templates call useSearchParams(), and Next defers any boundary enclosing
				  that during SSR, which put `<!--$?-->` and the empty shell straight back.
				  Lazy children own their boundaries instead — see LottieAnimation.
				*/}
				<LocationProvider location={location}>{content}</LocationProvider>
			</GameModeContext.Provider>
		</EDSProvider>
	);
};
